/**
 * Flow-field crowd simulation — the browser port of Swarmhold's navigation.
 *
 * Two ideas carry the whole thing:
 *
 *  1. Pathfinding is solved ONCE per goal, for the whole grid, not once per
 *     agent. A Dijkstra pass produces an integration field (cost-to-goal per
 *     cell); the gradient of that field is a direction every agent can read
 *     with a single lookup. Cost is O(cells), not O(agents) — the ten-thousandth
 *     agent is free.
 *
 *  2. Agent state lives in flat typed arrays, not objects. Structure-of-arrays
 *     keeps the hot loop linear in memory, which is what makes tens of
 *     thousands of agents tractable in a scripting language at all.
 *
 * Separation uses a uniform spatial hash rebuilt each step with a counting
 * sort — no allocation in the frame loop.
 */

export type SimConfig = {
  cols: number;
  rows: number;
  cellSize: number;
  maxAgents: number;
};

export type Turret = {
  /** World position. */
  x: number;
  y: number;
  /** Acquisition radius, world units. */
  range: number;
  /** Seconds between shots. */
  interval: number;
  /** Countdown to the next shot. */
  cooldown: number;
  /** Current facing, radians. Eased toward the target each step. */
  angle: number;
  /** Agent index currently being tracked, or -1. */
  target: number;
  /** Seconds since the last shot — drives the muzzle flash. */
  sinceFire: number;
};

const SQRT2 = Math.SQRT2;
const UNREACHED = 0xffffffff;

export class FlowFieldSim {
  readonly cols: number;
  readonly rows: number;
  readonly cellSize: number;
  readonly maxAgents: number;

  /** 1 where the cell is blocked. */
  readonly walls: Uint8Array;
  /** Dijkstra cost-to-goal, in grid units. UNREACHED where no path exists. */
  readonly cost: Float32Array;
  /** Unit direction toward the goal, interleaved x,y per cell. */
  readonly flow: Float32Array;

  // Structure-of-arrays agent state.
  readonly px: Float32Array;
  readonly py: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  readonly tint: Float32Array;
  count = 0;

  // Spatial hash (counting sort, rebuilt per step, never reallocated).
  private readonly cellStart: Int32Array;
  private readonly cellCount: Int32Array;
  private readonly sorted: Int32Array;

  // Shortest-path scratch. `queue` is a ring; `queued` keeps a cell from
  // sitting in it twice, which is what keeps the ring small and bounded.
  private readonly queue: Int32Array;
  private readonly queued: Uint8Array;
  /** Respawn PRNG state — keeps the frame loop allocation-free. */
  private rngState = 0x9e3779b9;

  // ---- turrets ------------------------------------------------------------
  // Fixed emplacements that acquire the nearest agent in range and fire a
  // homing shot at it. Acquisition reuses the same spatial hash the separation
  // pass builds, so adding turrets costs a bounded neighbourhood scan each,
  // not a sweep over the crowd.
  readonly turrets: Turret[] = [];
  /** Interleaved x, y, dirX, dirY, age per live projectile. */
  readonly shots: Float32Array;
  /** Target agent index per projectile slot; -1 when the slot is free. */
  readonly shotTarget: Int32Array;
  shotCount = 0;
  private readonly maxShots: number;

  /** Interleaved x, y, age for the kill flashes. */
  readonly impacts: Float32Array;
  impactCount = 0;
  private readonly maxImpacts: number;

  /** Rolling kill tally, read by the HUD. */
  kills = 0;

  private goalCell = -1;
  /** Wall clock of the last field rebuild, in ms. */
  lastFieldMs = 0;

  constructor(cfg: SimConfig) {
    this.cols = cfg.cols;
    this.rows = cfg.rows;
    this.cellSize = cfg.cellSize;
    this.maxAgents = cfg.maxAgents;

    const cells = cfg.cols * cfg.rows;
    this.walls = new Uint8Array(cells);
    this.cost = new Float32Array(cells);
    this.flow = new Float32Array(cells * 2);

    this.px = new Float32Array(cfg.maxAgents);
    this.py = new Float32Array(cfg.maxAgents);
    this.vx = new Float32Array(cfg.maxAgents);
    this.vy = new Float32Array(cfg.maxAgents);
    this.tint = new Float32Array(cfg.maxAgents);

    this.cellStart = new Int32Array(cells + 1);
    this.cellCount = new Int32Array(cells);
    this.sorted = new Int32Array(cfg.maxAgents);

    this.queue = new Int32Array(cells + 1);
    this.queued = new Uint8Array(cells);

    this.maxShots = 4096;
    this.shots = new Float32Array(this.maxShots * 5);
    this.shotTarget = new Int32Array(this.maxShots).fill(-1);

    this.maxImpacts = 256;
    this.impacts = new Float32Array(this.maxImpacts * 3);

    this.cost.fill(UNREACHED);
  }

  get worldWidth() {
    return this.cols * this.cellSize;
  }
  get worldHeight() {
    return this.rows * this.cellSize;
  }

  // ---------------------------------------------------------------- obstacles

  clearWalls() {
    this.walls.fill(0);
    this.goalCell = -1;
  }

  /** Stamp an axis-aligned block of cells, in cell coordinates. */
  stampRect(cx: number, cy: number, w: number, h: number) {
    const x0 = Math.max(0, Math.round(cx - w / 2));
    const y0 = Math.max(0, Math.round(cy - h / 2));
    const x1 = Math.min(this.cols - 1, Math.round(cx + w / 2));
    const y1 = Math.min(this.rows - 1, Math.round(cy + h / 2));
    for (let y = y0; y <= y1; y++) {
      const row = y * this.cols;
      for (let x = x0; x <= x1; x++) this.walls[row + x] = 1;
    }
    this.goalCell = -1;
  }

  // ------------------------------------------------------------- agent spawns

  /** Scatter n agents into open cells, biased toward the outside edges. */
  populate(n: number, rng: () => number) {
    const target = Math.min(n, this.maxAgents);
    // Shrinking is free: the tail is simply never read.
    if (target <= this.count) {
      this.count = target;
      return;
    }
    const W = this.worldWidth;
    const H = this.worldHeight;
    for (let i = this.count; i < target; i++) {
      let x = 0;
      let y = 0;
      // Reject into open space; the map is mostly open so this converges fast.
      for (let tries = 0; tries < 24; tries++) {
        const edge = rng();
        if (edge < 0.5) {
          x = rng() * W;
          y = rng() < 0.5 ? rng() * H * 0.18 : H - rng() * H * 0.18;
        } else {
          x = rng() < 0.5 ? rng() * W * 0.18 : W - rng() * W * 0.18;
          y = rng() * H;
        }
        if (!this.isBlockedWorld(x, y)) break;
      }
      this.px[i] = x;
      this.py[i] = y;
      this.vx[i] = 0;
      this.vy[i] = 0;
      this.tint[i] = rng();
    }
    this.count = target;
  }

  private isBlockedWorld(x: number, y: number) {
    const cx = (x / this.cellSize) | 0;
    const cy = (y / this.cellSize) | 0;
    if (cx < 0 || cy < 0 || cx >= this.cols || cy >= this.rows) return true;
    return this.walls[cy * this.cols + cx] === 1;
  }

  // ------------------------------------------------------------- the flow field

  /**
   * Single-source shortest path out from the goal across the whole grid, then
   * differentiate into directions.
   *
   * Straight moves cost 1, diagonals cost sqrt(2), and a diagonal is refused
   * when it would cut a wall corner. With only two edge weights a
   * queue-relaxation pass (SPFA) settles in a couple of sweeps and needs no
   * heap — the ring holds at most one entry per cell because `queued` gates
   * re-insertion.
   */
  rebuildField(goalX: number, goalY: number): boolean {
    const gx = Math.min(
      this.cols - 1,
      Math.max(0, (goalX / this.cellSize) | 0),
    );
    const gy = Math.min(
      this.rows - 1,
      Math.max(0, (goalY / this.cellSize) | 0),
    );
    const goal = gy * this.cols + gx;
    if (goal === this.goalCell) return false;

    const t0 = performance.now();
    const { cols, rows, cost, walls, queued, queue } = this;
    const cells = cols * rows;
    const ring = cells + 1;

    cost.fill(UNREACHED);
    queued.fill(0);

    // If the goal landed inside a wall, walk outward to the nearest open cell.
    let start = goal;
    if (walls[start] === 1) {
      let best = -1;
      let bestD = Infinity;
      for (let i = 0; i < cells; i++) {
        if (walls[i] === 1) continue;
        const dx = (i % cols) - gx;
        const dy = ((i / cols) | 0) - gy;
        const d = dx * dx + dy * dy;
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      if (best < 0) return false;
      start = best;
    }

    let head = 0;
    let tail = 0;
    cost[start] = 0;
    queued[start] = 1;
    queue[tail++] = start;

    while (head !== tail) {
      const cur = queue[head++];
      if (head === ring) head = 0;
      queued[cur] = 0;

      const cx = cur % cols;
      const cy = (cur / cols) | 0;
      const base = cost[cur];

      for (let oy = -1; oy <= 1; oy++) {
        const ny = cy + oy;
        if (ny < 0 || ny >= rows) continue;
        for (let ox = -1; ox <= 1; ox++) {
          if (ox === 0 && oy === 0) continue;
          const nx = cx + ox;
          if (nx < 0 || nx >= cols) continue;
          const ni = ny * cols + nx;
          if (walls[ni] === 1) continue;

          let step = 1;
          if (ox !== 0 && oy !== 0) {
            // No corner cutting: both orthogonal neighbours must be open.
            if (walls[cy * cols + nx] === 1 || walls[ny * cols + cx] === 1)
              continue;
            step = SQRT2;
          }
          const nc = base + step;
          if (nc < cost[ni]) {
            cost[ni] = nc;
            if (queued[ni] === 0) {
              queued[ni] = 1;
              queue[tail++] = ni;
              if (tail === ring) tail = 0;
            }
          }
        }
      }
    }

    this.differentiate();
    this.goalCell = goal;
    this.lastFieldMs = performance.now() - t0;
    return true;
  }

  /** Central-difference the cost field into a unit direction per cell. */
  private differentiate() {
    const { cols, rows, cost, flow, walls } = this;
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const i = y * cols + x;
        const o = i * 2;
        if (walls[i] === 1 || cost[i] === UNREACHED) {
          flow[o] = 0;
          flow[o + 1] = 0;
          continue;
        }
        const c = cost[i];
        const l = x > 0 ? this.sampleCost(i - 1, c) : c;
        const r = x < cols - 1 ? this.sampleCost(i + 1, c) : c;
        const u = y > 0 ? this.sampleCost(i - cols, c) : c;
        const d = y < rows - 1 ? this.sampleCost(i + cols, c) : c;
        // Downhill is toward lower cost.
        let dx = l - r;
        let dy = u - d;
        const len = Math.hypot(dx, dy);
        if (len > 1e-6) {
          dx /= len;
          dy /= len;
        } else {
          dx = 0;
          dy = 0;
        }
        flow[o] = dx;
        flow[o + 1] = dy;
      }
    }
  }

  private sampleCost(i: number, fallback: number) {
    const c = this.cost[i];
    return this.walls[i] === 1 || c === UNREACHED ? fallback + 1.5 : c;
  }

  // ------------------------------------------------------------------- stepping

  /**
   * Advance every agent one fixed step.
   *
   * @param dt      seconds, clamped by the caller
   * @param speed   world units per second
   * @param separation 0..1 weight on the neighbour-avoidance term
   */
  step(dt: number, speed: number, separation: number) {
    const n = this.count;
    if (n === 0) return;

    const { px, py, vx, vy, cellSize, cols, rows, flow } = this;
    const W = this.worldWidth;
    const H = this.worldHeight;

    this.rebuildHash();

    // Turrets and projectiles both read the hash, so they run while it is
    // fresh and before agents move for this step.
    this.stepTurrets(dt);
    this.stepShots(dt);
    this.stepImpacts(dt);

    const sepRadius = cellSize * 0.85;
    const sepR2 = sepRadius * sepRadius;
    const accel = 9;

    for (let i = 0; i < n; i++) {
      const x = px[i];
      const y = py[i];

      // --- flow lookup, bilinear across the four surrounding cells ----------
      const fx = x / cellSize - 0.5;
      const fy = y / cellSize - 0.5;
      let x0 = Math.floor(fx);
      let y0 = Math.floor(fy);
      const tx = fx - x0;
      const ty = fy - y0;
      if (x0 < 0) x0 = 0;
      if (y0 < 0) y0 = 0;
      const x1 = x0 + 1 < cols ? x0 + 1 : x0;
      const y1 = y0 + 1 < rows ? y0 + 1 : y0;
      const i00 = (y0 * cols + x0) * 2;
      const i10 = (y0 * cols + x1) * 2;
      const i01 = (y1 * cols + x0) * 2;
      const i11 = (y1 * cols + x1) * 2;
      const w00 = (1 - tx) * (1 - ty);
      const w10 = tx * (1 - ty);
      const w01 = (1 - tx) * ty;
      const w11 = tx * ty;

      let dx =
        flow[i00] * w00 + flow[i10] * w10 + flow[i01] * w01 + flow[i11] * w11;
      let dy =
        flow[i00 + 1] * w00 +
        flow[i10 + 1] * w10 +
        flow[i01 + 1] * w01 +
        flow[i11 + 1] * w11;

      // --- separation, over the 3x3 neighbourhood of the spatial hash -------
      if (separation > 0) {
        let sx = 0;
        let sy = 0;
        const gx = (x / cellSize) | 0;
        const gy = (y / cellSize) | 0;
        const gx0 = gx > 0 ? gx - 1 : 0;
        const gy0 = gy > 0 ? gy - 1 : 0;
        const gx1 = gx < cols - 1 ? gx + 1 : cols - 1;
        const gy1 = gy < rows - 1 ? gy + 1 : rows - 1;

        for (let cy = gy0; cy <= gy1; cy++) {
          const rowBase = cy * cols;
          for (let cx = gx0; cx <= gx1; cx++) {
            const cell = rowBase + cx;
            const s = this.cellStart[cell];
            const e = s + this.cellCount[cell];
            for (let k = s; k < e; k++) {
              const j = this.sorted[k];
              if (j === i) continue;
              const ox = x - px[j];
              const oy = y - py[j];
              const d2 = ox * ox + oy * oy;
              if (d2 > sepR2 || d2 < 1e-6) continue;
              const inv = 1 / d2;
              sx += ox * inv;
              sy += oy * inv;
            }
          }
        }
        const sl = Math.hypot(sx, sy);
        if (sl > 1e-6) {
          dx += (sx / sl) * separation;
          dy += (sy / sl) * separation;
        }
      }

      const dl = Math.hypot(dx, dy);
      if (dl > 1e-6) {
        dx /= dl;
        dy /= dl;
      }

      // --- integrate --------------------------------------------------------
      const k = Math.min(1, accel * dt);
      const nvx = vx[i] + (dx * speed - vx[i]) * k;
      const nvy = vy[i] + (dy * speed - vy[i]) * k;
      vx[i] = nvx;
      vy[i] = nvy;

      let nx = x + nvx * dt;
      let ny = y + nvy * dt;

      // --- resolve against walls, axis at a time so grazing still slides ----
      if (this.isBlockedWorld(nx, y)) nx = x;
      if (this.isBlockedWorld(nx, ny)) ny = y;

      // --- keep the field populated: recycle anything that reaches the goal
      //     or wanders off the map back out to an edge ------------------------
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) {
        this.respawn(i);
        continue;
      }
      const ci = ((ny / cellSize) | 0) * cols + ((nx / cellSize) | 0);
      if (this.cost[ci] < 2.2) {
        this.respawn(i);
        continue;
      }

      px[i] = nx;
      py[i] = ny;
    }
  }

  // ------------------------------------------------------------------ turrets

  clearTurrets() {
    this.turrets.length = 0;
  }

  addTurret(x: number, y: number, range: number, interval: number) {
    this.turrets.push({
      x,
      y,
      range,
      interval,
      // Stagger the opening volley so they do not all fire on the same tick.
      cooldown: this.nextRandom() * interval,
      angle: 0,
      target: -1,
      sinceFire: 99,
    });
  }

  /**
   * Nearest agent to (x, y) within `range`, or -1.
   *
   * Walks only the hash cells the range actually covers. This is why the
   * spatial hash earns its keep twice: separation needs it, and so does every
   * turret's acquisition — neither has to touch the full crowd.
   */
  private acquire(x: number, y: number, range: number): number {
    const { cellSize, cols, rows, px, py } = this;
    const r2 = range * range;
    const span = Math.ceil(range / cellSize);

    let gx = (x / cellSize) | 0;
    let gy = (y / cellSize) | 0;
    if (gx < 0) gx = 0;
    else if (gx >= cols) gx = cols - 1;
    if (gy < 0) gy = 0;
    else if (gy >= rows) gy = rows - 1;

    let best = -1;
    let bestD = r2;

    // Expand ring by ring and stop at the first ring that yields a target.
    // Scanning the whole range box is correct but quadratic in radius, which
    // does not survive two hundred turrets. In a dense crowd the first ring
    // or two almost always hits, and "nearest in the innermost occupied ring"
    // is indistinguishable on screen from "nearest overall".
    for (let r = 0; r <= span; r++) {
      const x0 = Math.max(0, gx - r);
      const x1 = Math.min(cols - 1, gx + r);
      const y0 = Math.max(0, gy - r);
      const y1 = Math.min(rows - 1, gy + r);

      for (let cy = y0; cy <= y1; cy++) {
        const onEdgeRow = cy === gy - r || cy === gy + r;
        const rowBase = cy * cols;
        // Interior cells were covered by a smaller ring already.
        const step = onEdgeRow ? 1 : x1 - x0 === 0 ? 1 : x1 - x0;
        for (let cx = x0; cx <= x1; cx += step) {
          const cell = rowBase + cx;
          const start = this.cellStart[cell];
          const end = start + this.cellCount[cell];
          for (let k = start; k < end; k++) {
            const j = this.sorted[k];
            const dx = px[j] - x;
            const dy = py[j] - y;
            const d2 = dx * dx + dy * dy;
            if (d2 < bestD) {
              bestD = d2;
              best = j;
            }
          }
          if (step === 0) break;
        }
      }
      if (best >= 0) return best;
    }
    return best;
  }

  /** Acquire, turn, and fire. Assumes the hash is fresh for this step. */
  private stepTurrets(dt: number) {
    const turrets = this.turrets;
    for (let i = 0; i < turrets.length; i++) {
      const t = turrets[i];
      t.sinceFire += dt;

      // Re-acquire when the current target is gone or out of range.
      let keep = false;
      if (t.target >= 0 && t.target < this.count) {
        const dx = this.px[t.target] - t.x;
        const dy = this.py[t.target] - t.y;
        keep = dx * dx + dy * dy <= t.range * t.range;
      }
      if (!keep) t.target = this.acquire(t.x, t.y, t.range);

      if (t.target < 0) {
        t.cooldown = Math.min(t.cooldown, t.interval * 0.35);
        continue;
      }

      // Ease the barrel toward the target across the shortest arc.
      const want = Math.atan2(this.py[t.target] - t.y, this.px[t.target] - t.x);
      let delta = want - t.angle;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      t.angle += delta * Math.min(1, 12 * dt);

      t.cooldown -= dt;
      if (t.cooldown <= 0 && Math.abs(delta) < 0.5) {
        t.cooldown = t.interval;
        t.sinceFire = 0;
        this.fire(t.x, t.y, t.angle, t.target);
      }
    }
  }

  private fire(x: number, y: number, angle: number, target: number) {
    if (this.shotCount >= this.maxShots) return;
    const o = this.shotCount * 5;
    this.shots[o] = x;
    this.shots[o + 1] = y;
    this.shots[o + 2] = Math.cos(angle);
    this.shots[o + 3] = Math.sin(angle);
    this.shots[o + 4] = 0;
    this.shotTarget[this.shotCount] = target;
    this.shotCount++;
  }

  /** Move projectiles, resolve hits, retire anything expired. Swap-removes. */
  private stepShots(dt: number) {
    const speed = this.cellSize * 19;
    const hitR = this.cellSize * 0.9;
    const hitR2 = hitR * hitR;
    const maxAge = 1.1;

    let i = 0;
    while (i < this.shotCount) {
      const o = i * 5;
      const target = this.shotTarget[i];

      // Steer toward the target so shots visibly track a moving crowd.
      if (target >= 0 && target < this.count) {
        const tx = this.px[target] - this.shots[o];
        const ty = this.py[target] - this.shots[o + 1];
        const len = Math.hypot(tx, ty);
        if (len > 1e-4) {
          const turn = Math.min(1, 9 * dt);
          this.shots[o + 2] += (tx / len - this.shots[o + 2]) * turn;
          this.shots[o + 3] += (ty / len - this.shots[o + 3]) * turn;
          const dl = Math.hypot(this.shots[o + 2], this.shots[o + 3]);
          if (dl > 1e-6) {
            this.shots[o + 2] /= dl;
            this.shots[o + 3] /= dl;
          }
        }
      }

      this.shots[o] += this.shots[o + 2] * speed * dt;
      this.shots[o + 1] += this.shots[o + 3] * speed * dt;
      this.shots[o + 4] += dt;

      let done = this.shots[o + 4] > maxAge;

      if (!done && target >= 0 && target < this.count) {
        const dx = this.px[target] - this.shots[o];
        const dy = this.py[target] - this.shots[o + 1];
        if (dx * dx + dy * dy <= hitR2) {
          this.addImpact(this.px[target], this.py[target]);
          this.respawn(target);
          this.kills++;
          done = true;
        }
      }

      if (done) {
        this.shotCount--;
        const last = this.shotCount * 5;
        for (let k = 0; k < 5; k++) this.shots[o + k] = this.shots[last + k];
        this.shotTarget[i] = this.shotTarget[this.shotCount];
        this.shotTarget[this.shotCount] = -1;
      } else {
        i++;
      }
    }
  }

  private addImpact(x: number, y: number) {
    if (this.impactCount >= this.maxImpacts) return;
    const o = this.impactCount * 3;
    this.impacts[o] = x;
    this.impacts[o + 1] = y;
    this.impacts[o + 2] = 0;
    this.impactCount++;
  }

  private stepImpacts(dt: number) {
    const life = 0.35;
    let i = 0;
    while (i < this.impactCount) {
      const o = i * 3;
      this.impacts[o + 2] += dt;
      if (this.impacts[o + 2] >= life) {
        this.impactCount--;
        const last = this.impactCount * 3;
        for (let k = 0; k < 3; k++)
          this.impacts[o + k] = this.impacts[last + k];
      } else {
        i++;
      }
    }
  }

  /** Cheap xorshift — inlined so the frame loop never touches the heap. */
  private nextRandom() {
    let x = this.rngState;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.rngState = x;
    return (x >>> 0) / 4294967296;
  }

  /** Send an agent back out to a random edge so the field stays populated. */
  private respawn(i: number) {
    const W = this.worldWidth;
    const H = this.worldHeight;
    const a = this.nextRandom();
    const b = this.nextRandom();
    const side = (a * 4) | 0;
    let x: number;
    let y: number;
    if (side === 0) {
      x = b * W;
      y = 1;
    } else if (side === 1) {
      x = b * W;
      y = H - 1;
    } else if (side === 2) {
      x = 1;
      y = b * H;
    } else {
      x = W - 1;
      y = b * H;
    }
    if (this.isBlockedWorld(x, y)) {
      x = W * 0.5;
      y = 1;
    }
    this.px[i] = x;
    this.py[i] = y;
    this.vx[i] = 0;
    this.vy[i] = 0;
  }

  /** Counting sort of agent indices into grid cells. Zero allocation. */
  private rebuildHash() {
    const { cellCount, cellStart, sorted, px, py, cellSize, cols, rows } = this;
    const cells = cols * rows;
    const n = this.count;

    cellCount.fill(0);
    for (let i = 0; i < n; i++) {
      let cx = (px[i] / cellSize) | 0;
      let cy = (py[i] / cellSize) | 0;
      if (cx < 0) cx = 0;
      else if (cx >= cols) cx = cols - 1;
      if (cy < 0) cy = 0;
      else if (cy >= rows) cy = rows - 1;
      cellCount[cy * cols + cx]++;
    }

    let acc = 0;
    for (let c = 0; c < cells; c++) {
      cellStart[c] = acc;
      acc += cellCount[c];
      cellCount[c] = 0;
    }
    cellStart[cells] = acc;

    for (let i = 0; i < n; i++) {
      let cx = (px[i] / cellSize) | 0;
      let cy = (py[i] / cellSize) | 0;
      if (cx < 0) cx = 0;
      else if (cx >= cols) cx = cols - 1;
      if (cy < 0) cy = 0;
      else if (cy >= rows) cy = rows - 1;
      const c = cy * cols + cx;
      sorted[cellStart[c] + cellCount[c]] = i;
      cellCount[c]++;
    }
  }
}

/** Tiny deterministic PRNG so layouts are stable across reloads. */
export function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
